<script lang="ts">
	//Hooks
	import { onMount } from 'svelte';
	//Formatters
	import { format } from '../../../../services/i18n';
	//Requests
	import { localReq, postReq } from '../../../../services/http';
	//Helpers
	import { get_local_ip, real_id } from '../../../../helpers/interface_helpers';
	import type { Device } from 'src/types/Device';
	//Stores
		import { readDeviceSettings } from 'src/stores/device-runtime/status-selectors';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';
	//Design system components
	import { Button, toast, Checkbox, Container, TimeWheel } from '@Shelly/shelly-design-system';
	import {
		convertSeconds,
		convertToSeconds,
		formatTwoDigits,
		formatTimeLabel,
		getTimeWheelTexts,
		getTimeWheelMsStep,
		getTimeWheelToggleText,
	} from '../../../../helpers/timer_helpers';

	export let device: Device;

	let values = { on: 0, off: 0 },
		subsettings = ['on', 'off'],
		id = real_id(device.id);

	let settingsPerChannel;
	let min = 0,
		max = 65535,
		localIp = get_local_ip(id),
		loading = { on: false, off: false };

	let selectedHour = { on: '00', off: '00' };
	let selectedMinute = { on: '00', off: '00' };
	let selectedSecond = { on: '00', off: '00' };
	let selectedMs = { auto_on_delay: undefined, auto_off_delay: undefined };
	let maxTimes = convertSeconds(max);

	$: infoText = `${$format('timer_info_description', {
		values: {
			min: min,
			max: formatTimeLabel(
				maxTimes.hours,
				maxTimes.minutes,
				maxTimes.seconds,
				maxTimes.milliseconds,
			),
		},
	})}`;

	$: checkBoxInfoText = {
		on: `${$format('auto_on_description')} 
					<span class='text-primary text-bold'>
						${formatTimeLabel(selectedHour['on'], selectedMinute['on'], selectedSecond['on'], selectedMs['on'])}
					</span>`,
		off: `${$format('auto_off_description')}
					<span class='text-primary text-bold'>
						${formatTimeLabel(selectedHour['off'], selectedMinute['off'], selectedSecond['off'], selectedMs['off'])}
					</span>`,
	};

	$: errors = {
		on:
			convertToSeconds(
				selectedHour['on'],
				selectedMinute['on'],
				selectedSecond['on'],
				selectedMs['on'],
			) > max
				? infoText
				: '',
		off:
			convertToSeconds(
				selectedHour['off'],
				selectedMinute['off'],
				selectedSecond['off'],
				selectedMs['off'],
			) > max
				? infoText
				: '',
	};

	onMount(() => {
		extractSettings();
	});

	const getTimeInSeconds = (type: 'on' | 'off') => {
		return convertToSeconds(
			selectedHour[type],
			selectedMinute[type],
			selectedSecond[type],
			selectedMs[type],
		);
	};

	const updateTimeValues = (type: 'on' | 'off') => {
		const { hours, minutes, seconds, milliseconds } = convertSeconds(values[type] || 0);
		selectedHour[type] = formatTwoDigits(hours);
		selectedMinute[type] = formatTwoDigits(minutes);
		selectedSecond[type] = formatTwoDigits(seconds);
		selectedMs[type] = milliseconds ? formatTwoDigits(milliseconds) : undefined;
	};

	const extractSettings = () => {
		const deviceSettings = readDeviceSettings(id);
		if (device.category === 'emeter') {
			// handle the case one ralay yet 2 possible channels
			if (deviceSettings?.['relays']?.[0]) {
				settingsPerChannel = deviceSettings['relays'][0];
				subsettings.forEach((option) => {
					values[option] = settingsPerChannel['auto_' + option];
				});
			}
		} else {
			if (deviceSettings?.[device.category + 's']?.[device.channel]) {
				settingsPerChannel = deviceSettings[device.category + 's'][device.channel];
				subsettings.forEach((option) => {
					values[option] = settingsPerChannel['auto_' + option];
				});
			}
		}

		updateTimeValues('on');
		updateTimeValues('off');

		return settingsPerChannel;
	};

	const setAuto = async (setting) => {
		let res;
		loading[setting] = true;

		const secconds = getTimeInSeconds(setting);
		let value = secconds;
		if (localIp) {
			res = await localReq(
				device,
				`${localIp}/settings/${device.category === 'emeter' ? 'relay' : device.category}/${device.channel}`,
				'post',
				{
					[`auto_${setting === 'on' ? 'on' : 'off'}`]: value,
				},
			);
		} else {
			if (device.category !== 'emeter') {
				res = await postReq(`/device/${device.category}/settings/auto_turn`, {
					id: id,
					channel: device.channel,
					timeout: value,
					state: setting,
				});
			} else {
				res = await postReq(`/device/relay/settings/auto_turn`, {
					id: id,
					channel: 0,
					timeout: value,
					state: setting,
				});
			}
		}
		if (res.isok) {
			toast.open({
				type: 'success',
				title:
					value > 0
						? `${$format(`auto_${setting}_set_toast`)} ${formatTimeLabel(
								selectedHour[setting],
								selectedMinute[setting],
								selectedSecond[setting],
								selectedMs[setting],
							)}.`
						: $format('auto_turn_successfully_disable', {
								values: { auto_turn_state: $format(`auto_${setting}`) },
							}),
			});
		} else {
			toast.open({ type: 'danger', title: $format('timer_err_toast') });
		}
		loading[setting] = false;
	};

	const handleToggler = (type) => {
		const secconds = getTimeInSeconds(type);

		values[type] = secconds === 0 ? 30 : 0;
		updateTimeValues(type);
	};
</script>

{#each subsettings as setting}
	<Container
		backgroundColor={$showNewLayout ? 'color-0' : undefined}
		type={!$showNewLayout ? 'modalContainer' : undefined}
		padding="xs"
		class="mb-s border-color-transparent"
	>
		<Checkbox
			class="mb-s"
			size="m"
			info={checkBoxInfoText[setting]}
			checked={selectedHour[setting] != '00' ||
				selectedMinute[setting] != '00' ||
				selectedSecond[setting] != '00' ||
				![undefined, '00'].includes(selectedMs[setting])}
			on:click={() => handleToggler(setting)}
			id="checkbox-yz44jem"
		>
			{$format('auto_' + setting)}
		</Checkbox>

		<TimeWheel
			id="timewheel-qyxd9g1"
			a11yProps={{
				inputAriaLabel: $format('label_time_wheel_input', { default: 'Time wheel input' }),
				buttonAriaLabel: $format('label_time_wheel_button', { default: 'Time wheel button' }),
				secondsColumnAriaLabel: $format('label_time_wheel_seconds_column', {
					default: 'Time wheel seconds column',
				}),
				secondPrefixAriaLabel: $format('label_time_wheel_second_prefix', {
					default: 'Time wheel second prefix',
				}),
				msColumnAriaLabel: $format('label_time_wheel_ms_column', {
					default: 'Time wheel ms column',
				}),
				msPrefixAriaLabel: $format('label_time_wheel_ms_prefix', {
					default: 'Time wheel ms prefix',
				}),
				hoursColumnAriaLabel: $format('label_time_wheel_hours_column', {
					default: 'Time wheel hours column',
				}),
				hourPrefixAriaLabel: $format('label_time_wheel_hour_prefix', {
					default: 'Time wheel hour prefix',
				}),
				minutesColumnAriaLabel: $format('label_time_wheel_minutes_column', {
					default: 'Time wheel minutes column',
				}),
				minutePrefixAriaLabel: $format('label_time_wheel_minute_prefix', {
					default: 'Time wheel minute prefix',
				}),
			}}
			class="mb-s"
			bind:selectedHour={selectedHour[setting]}
			bind:selectedMinute={selectedMinute[setting]}
			bind:selectedSecond={selectedSecond[setting]}
			bind:selectedMs={selectedMs[setting]}
			maxHours={maxTimes.hours}
			msToggleText={getTimeWheelToggleText()}
			info={infoText}
			error={errors[`${setting}`]}
			texts={getTimeWheelTexts()}
			msStep={getTimeWheelMsStep(device.type)}
		/>

		<Button
			size="m"
			variant={'primary'}
			width="full"
			loading={loading[setting]}
			disabled={loading[setting] || errors[`${setting}`]}
			on:click={() => setAuto(setting)}
		>
			{$format('button_save')}
		</Button>
	</Container>
{/each}
